/* Pet Nugget "Can my dog eat...?" lookup tool. Pure helpers are exported for Node tests
   (tests/pn-food-tool.test.js); in the browser every [data-pn-food] block on the page is mounted.

   Data: the block embeds its own JSON (records from the food_safety metaobjects, active ones only,
   rendered by snippets/pn-food-tool.liquid). Nothing here invents a verdict: a food that is not in
   that list gets "we haven't reviewed this one yet" plus the poison-control line, never a guess.

   The answer always shows at once. Klaviyo is fire and forget and never blocks or gates an answer.
   The dog (name, weight, foods checked) is remembered in this browser only, wrapped in try/catch. */
(function (host, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!host || !host.document) return;
  host.PNFoodTool = api;
  function boot(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-pn-food]').forEach(function (element) { api.mount(element, host); });
  }
  if (host.document.readyState === 'loading') host.document.addEventListener('DOMContentLoaded', function () { boot(host.document); });
  else boot(host.document);
  host.document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  var VERDICTS = ['yes', 'little', 'skip', 'toxic'];
  var VERDICT_LABEL = { yes: 'Yes', little: 'A little', skip: 'Skip it', toxic: 'Toxic' };
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var ID_RE = /^[a-zA-Z0-9]{6}$/;
  var KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions?company_id=';
  var STORE_KEY = 'pn-dog';
  var MAX_WEIGHT = 250;
  // Phrases people type around the food name. Removed before matching (longest first).
  var FILLER = [
    'is it ok for dogs to eat', 'is it safe for dogs to eat', 'can my puppy eat', 'can puppies eat', 'can my dog drink', 'can my dog have',
    'can my dog eat', 'can dogs drink', 'can dogs have', 'can dogs eat', 'can dog eat', 'can a dog eat', 'are dogs allowed', 'my dog just ate', 'my dog ate',
    'safe for dogs', 'ok for dogs', 'okay for dogs', 'good for dogs', 'bad for dogs', 'toxic to dogs', 'poisonous to dogs', 'for dogs',
    'is', 'are', 'dogs', 'dog', 'a', 'an', 'the', 'some', 'any'
  ];

  /* ---------------- Pure helpers ---------------- */
  function fold(text) {
    return String(text == null ? '' : text).toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .replace(/&/g, ' and ').replace(/['’]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }
  function singular(word) {
    if (word.length > 4 && /ies$/.test(word)) return word.slice(0, -3) + 'y';
    if (word.length > 4 && /(oes|ches|shes|sses|xes)$/.test(word)) return word.slice(0, -2);
    if (word.length > 3 && /s$/.test(word) && !/(ss|us|is)$/.test(word)) return word.slice(0, -1);
    return word;
  }
  // "Can dogs eat raw eggs?" -> "raw egg"
  function normalize(query) {
    var text = ' ' + fold(query) + ' ';
    FILLER.forEach(function (phrase) { text = text.split(' ' + phrase + ' ').join(' '); });
    return text.trim().split(/\s+/).filter(Boolean).map(singular).join(' ');
  }
  function distance(a, b) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > 2) return 3;
    var prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
      }
      prev = cur;
    }
    return prev[b.length];
  }
  function keysFor(record) {
    if (record._keys) return record._keys;
    var names = [record.n].concat(Array.isArray(record.a) ? record.a : []);
    var keys = [];
    names.forEach(function (name) { var key = normalize(name); if (key && keys.indexOf(key) === -1) keys.push(key); });
    try { Object.defineProperty(record, '_keys', { value: keys, enumerable: false }); } catch (_) {}
    return keys;
  }
  function scoreKey(query, key) {
    if (!query || !key) return 0;
    if (key === query) return 100;
    if (key.indexOf(query) === 0) return 80 - Math.min(20, key.length - query.length);
    var words = key.split(' ');
    if (words.some(function (word) { return word.indexOf(query) === 0; })) return 60;
    // The query names the food inside a longer phrase: "raw egg" -> egg, "turkey skin" -> turkey.
    if (query.split(' ').length > 1 && key.length >= 3 && (' ' + query + ' ').indexOf(' ' + key + ' ') !== -1) return 55;
    if (query.length >= 3 && key.indexOf(query) !== -1) return 45;
    if (query.length >= 4) {
      var d = distance(query, key);
      if (d === 1) return 35;
      if (d === 2 && query.length >= 7) return 25;
      if (words.some(function (word) { return word.length >= 4 && distance(query, word) === 1; })) return 30;
    }
    return 0;
  }
  // Records sorted best first, each scored on its best key.
  function search(query, records, limit) {
    var q = normalize(query);
    if (!q) return [];
    var scored = [];
    (records || []).forEach(function (record) {
      var best = 0;
      keysFor(record).forEach(function (key) { best = Math.max(best, scoreKey(q, key)); });
      if (best > 0) scored.push({ record: record, score: best });
    });
    scored.sort(function (a, b) { return b.score - a.score || String(a.record.n).localeCompare(String(b.record.n)); });
    return scored.slice(0, limit || 8).map(function (item) { return item.record; });
  }
  // How sure a submitted query is about one key. Only these count as an answer: the exact name, the food
  // named inside a longer phrase ("raw eggs" -> eggs) or a near-miss typo ("turky"). A query that is only
  // the start of a longer name ("chicken" vs "chicken bones", "pizza" vs "pizza dough") is never an
  // answer: the visitor gets "not reviewed yet" with the close matches as suggestions instead.
  function confidentScore(query, key) {
    if (!query || !key) return 0;
    if (key === query) return 100;
    if (query.split(' ').length > 1 && key.length >= 3 && (' ' + query + ' ').indexOf(' ' + key + ' ') !== -1) return 50 + Math.min(40, key.length);
    if (query.length >= 4 && key.split(' ').length === query.split(' ').length) {
      var d = distance(query, key);
      if (d === 1) return 35;
      if (d === 2 && query.length >= 7) return 25;
    }
    return 0;
  }
  // The answer for a submitted query: one confident match, or nothing (then: "not reviewed yet").
  function bestMatch(query, records) {
    var q = normalize(query);
    if (!q) return null;
    var best = null, top = 0;
    (records || []).forEach(function (record) {
      keysFor(record).forEach(function (key) {
        var score = confidentScore(q, key);
        if (score > top) { top = score; best = record; }
      });
    });
    return top > 0 ? best : null;
  }
  function parseWeight(value) {
    var text = String(value == null ? '' : value).replace(/[^0-9.]/g, '');
    if (!text) return null;
    var lb = Number(text);
    if (!(lb > 0) || lb > MAX_WEIGHT) return NaN;
    return Math.round(lb * 10) / 10;
  }
  // The four bands on the Trello card, written to Klaviyo as pet_weight_band.
  function weightBand(lb) {
    if (!(lb > 0)) return '';
    if (lb <= 30) return '0-30';
    if (lb <= 60) return '31-60';
    if (lb <= 90) return '61-90';
    return '91+';
  }
  // Same size names the dog age calculator writes, so one dog is one profile.
  function sizeFor(lb) {
    if (!(lb > 0)) return '';
    if (lb <= 20) return 'small';
    if (lb <= 50) return 'medium';
    if (lb <= 100) return 'large';
    return 'giant';
  }
  // amounts: [{ label, min, max, text }] with min/max in lb. Rows given as single weights have min == max.
  function amountFor(amounts, lb) {
    if (!Array.isArray(amounts) || !amounts.length || !(lb > 0)) return null;
    var ranges = amounts.filter(function (row) { return (row.min != null || row.max != null) && Number(row.min) !== Number(row.max); });
    for (var i = 0; i < ranges.length; i++) {
      var min = ranges[i].min == null ? 0 : Number(ranges[i].min);
      var max = ranges[i].max == null ? Infinity : Number(ranges[i].max);
      if (lb >= min && lb <= max) return ranges[i];
    }
    // Single-weight rows (10, 20, 40 lb): the closest row at or under her weight, else the smallest.
    var points = amounts.filter(function (row) { return row.min != null && Number(row.min) === Number(row.max); })
      .sort(function (a, b) { return Number(a.min) - Number(b.min); });
    if (!points.length) return null;
    var pick = points[0];
    points.forEach(function (row) { if (Number(row.min) <= lb) pick = row; });
    return pick;
  }
  function possessive(name) {
    name = String(name || '').trim();
    if (!name) return '';
    return name + (/s$/i.test(name) ? '’' : '’s');
  }
  function question(record, name) {
    if (!record) return '';
    var who = String(name || '').trim() || 'my dog';
    var verb = record.c === 'drinks' ? 'drink' : 'eat';
    return 'Can ' + who + ' ' + verb + ' ' + (record.q || record.n) + '?';
  }
  function validEmail(value) { return EMAIL_RE.test(String(value || '').trim()) && String(value).length <= 254; }
  function validId(value) { return ID_RE.test(String(value == null ? '' : value)); }
  function safeUrl(value) {
    var url = String(value || '');
    return /^\/(?!\/)/.test(url) || /^https:\/\//.test(url) ? url : '';
  }

  // fields: { email, pet_name, weight_lb, food, verdict, unknown, foods_checked: [], placement }
  function subscriptionPayload(fields, listId) {
    fields = fields || {};
    var email = String(fields.email || '').trim();
    if (!validEmail(email) || !validId(listId)) throw new Error('Invalid signup');
    var properties = { petnugget_signup_source: 'dog_food_tool' };
    var name = String(fields.pet_name || '').trim().slice(0, 40);
    // Empty values are left out so they cannot blank what the profile already has (the age calculator may have set them).
    if (name) properties.pet_name = name;
    var lb = Number(fields.weight_lb);
    if (lb > 0 && lb <= MAX_WEIGHT) {
      properties.pet_weight_band = weightBand(lb);
      properties.pet_size = sizeFor(lb);
    }
    if (fields.food) {
      properties.last_food_checked = String(fields.food).slice(0, 80);
      if (VERDICTS.indexOf(fields.verdict) !== -1) properties.last_food_verdict = fields.verdict;
    }
    if (fields.unknown) properties.unknown_food_requested = String(fields.unknown).slice(0, 80);
    if (Array.isArray(fields.foods_checked) && fields.foods_checked.length) {
      properties.foods_checked = fields.foods_checked.slice(-40).map(function (food) { return String(food).slice(0, 80); });
    }
    properties.calculator_placement = ['tool', 'hub', 'article', 'category'].indexOf(fields.placement) !== -1 ? fields.placement : 'hub';
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Pet Nugget dog food safety tool',
          profile: { data: { type: 'profile', attributes: {
            email: email,
            properties: properties,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: String(listId) } } }
      }
    };
  }

  /* ---------------- Browser ---------------- */
  function mount(root, win) {
    win = win || (typeof window !== 'undefined' ? window : null);
    if (!root || !win || !root.dataset || root.dataset.pnFoodMounted) return;
    var doc = win.document;
    var dataNode = root.querySelector('[data-pn-food-data]');
    var form = root.querySelector('[data-pn-food-form]');
    var result = root.querySelector('[data-pn-food-result]');
    if (!dataNode || !form || !result) return;
    var data = {};
    try { data = JSON.parse(dataNode.textContent) || {}; } catch (_) { return; }
    // Hub and category pages load the food data after the page (data-src), to keep the HTML light.
    var src = dataNode.getAttribute && dataNode.getAttribute('data-src');
    if (src && !(Array.isArray(data.records) && data.records.length)) {
      if (root.dataset.pnFoodLoading || !win.fetch) return;
      root.dataset.pnFoodLoading = 'true';
      win.fetch(src, { credentials: 'same-origin' })
        .then(function (res) { return res.json(); })
        .then(function (list) {
          data.records = Array.isArray(list) ? list : [];
          dataNode.removeAttribute('data-src');
          dataNode.textContent = JSON.stringify(data);
          delete root.dataset.pnFoodLoading;
          mount(root, win);
        })
        .catch(function () { delete root.dataset.pnFoodLoading; });
      return;
    }
    var records = Array.isArray(data.records) ? data.records.filter(function (r) { return r && r.h && r.n && VERDICTS.indexOf(r.v) !== -1; }) : [];
    root.dataset.pnFoodMounted = 'true';
    var byHandle = {};
    records.forEach(function (r) { byHandle[r.h] = r; });

    var config = data.config || {};
    var placement = config.placement || 'hub';
    var emailEnabled = validId(config.companyId) && validId(config.listId);
    var poison = config.poison || {};
    var input = form.elements.food;
    var list = root.querySelector('[data-pn-food-list]');
    var nameInput = form.elements.pet_name;
    var weightInput = form.elements.weight_lb;
    var emailInput = emailEnabled ? form.elements.email : null;
    var checkedBox = root.querySelector('[data-pn-food-checked]');
    var uid = root.id || 'pn-food';
    var checked = [];
    var sentFor = {};
    var active = -1;
    var options = [];

    function el(tag, className, text) {
      var node = doc.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }
    function loadDog() {
      try { return JSON.parse(win.localStorage.getItem(STORE_KEY) || '{}') || {}; } catch (_) { return {}; }
    }
    function saveDog(dog) {
      try { win.localStorage.setItem(STORE_KEY, JSON.stringify(dog)); } catch (_) { /* Private mode: works without it. */ }
    }
    var remembered = loadDog();
    if (nameInput && remembered.name && !nameInput.value) nameInput.value = String(remembered.name).slice(0, 40);
    if (weightInput && remembered.lb && !weightInput.value) weightInput.value = String(remembered.lb);
    if (Array.isArray(remembered.foods)) checked = remembered.foods.filter(function (h) { return byHandle[h]; }).slice(-40);

    function dogName() { return nameInput ? String(nameInput.value || '').trim().slice(0, 40) : ''; }
    function dogWeight() {
      var lb = weightInput ? parseWeight(weightInput.value) : null;
      return lb > 0 ? lb : null;
    }
    function remember(record) {
      if (record && checked.indexOf(record.h) === -1) checked.push(record.h);
      checked = checked.slice(-40);
      saveDog({ name: dogName(), lb: dogWeight(), foods: checked });
    }
    function track(action, detail) {
      // Anonymous: the food and the date only. Never the email or the dog's name.
      var payload = { calculator: 'food-safety', action: action, food: detail.food || null, verdict: detail.verdict || null, query: detail.query || null, placement: placement, date: new Date().toISOString().slice(0, 10) };
      try { root.dispatchEvent(new win.CustomEvent('petnugget:calculator', { bubbles: true, detail: payload })); } catch (_) {}
      try { if (win.Shopify && win.Shopify.analytics && typeof win.Shopify.analytics.publish === 'function') win.Shopify.analytics.publish('pn_food_search', payload); } catch (_) {}
      try { if (typeof win.clarity === 'function') { win.clarity('event', 'food_' + action); if (payload.food) win.clarity('set', 'food_checked', payload.food); } } catch (_) {}
    }
    function setError(key, message) {
      var node = root.querySelector('[data-pn-food-error="' + key + '"]');
      var control = key === 'food' ? input : key === 'weight' ? weightInput : emailInput;
      if (node) { node.textContent = message; node.hidden = !message; }
      if (control) { if (message) control.setAttribute('aria-invalid', 'true'); else control.removeAttribute('aria-invalid'); }
    }
    function emailValue() {
      if (!emailInput) return '';
      var mail = String(emailInput.value || '').trim();
      return validEmail(mail) ? mail : '';
    }

    /* Autocomplete (ARIA combobox) */
    function closeList() {
      if (!list) return;
      list.hidden = true;
      list.textContent = '';
      options = [];
      active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    function highlight(index) {
      active = index;
      options.forEach(function (option, i) { option.setAttribute('aria-selected', i === index ? 'true' : 'false'); });
      if (index >= 0 && options[index]) input.setAttribute('aria-activedescendant', options[index].id);
      else input.removeAttribute('aria-activedescendant');
    }
    function openList(matches) {
      if (!list) return;
      list.textContent = '';
      options = [];
      matches.forEach(function (record, i) {
        var option = el('li', 'pn-food__option');
        option.id = uid + '-opt-' + i;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.handle = record.h;
        option.appendChild(el('span', 'pn-food__option-name', record.n));
        option.appendChild(el('span', 'pn-food__badge pn-food__badge--' + record.v, VERDICT_LABEL[record.v]));
        list.appendChild(option);
        options.push(option);
      });
      list.hidden = !options.length;
      input.setAttribute('aria-expanded', options.length ? 'true' : 'false');
      active = -1;
    }
    input.addEventListener('input', function () {
      if (!normalize(input.value)) { closeList(); return; }
      openList(search(input.value, records, 6));
    });
    input.addEventListener('keydown', function (event) {
      if (!list || list.hidden || !options.length) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); highlight((active + 1) % options.length); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); highlight(active <= 0 ? options.length - 1 : active - 1); }
      else if (event.key === 'Escape') closeList();
      else if (event.key === 'Enter' && active >= 0) { event.preventDefault(); choose(byHandle[options[active].dataset.handle]); }
    });
    if (list) {
      list.addEventListener('mousedown', function (event) { event.preventDefault(); });
      list.addEventListener('click', function (event) {
        var option = event.target.closest ? event.target.closest('[data-handle]') : null;
        if (option) choose(byHandle[option.dataset.handle]);
      });
    }
    input.addEventListener('blur', function () { win.setTimeout(closeList, 150); });
    function choose(record) {
      if (!record) return;
      input.value = record.n;
      closeList();
      answer(record);
    }

    // Quick picks, "check next" and "your list" chips.
    root.addEventListener('click', function (event) {
      var target = event.target.closest ? event.target.closest('[data-pn-food-pick]') : null;
      if (!target || !root.contains(target)) return;
      var record = byHandle[target.getAttribute('data-pn-food-pick')];
      if (!record) return;
      event.preventDefault();
      input.value = record.n;
      answer(record);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (weightInput) {
        var raw = String(weightInput.value || '').trim();
        setError('weight', raw && !(parseWeight(raw) > 0) ? 'Enter her weight in pounds, up to ' + MAX_WEIGHT + '.' : '');
      }
      if (emailInput) {
        var mail = String(emailInput.value || '').trim();
        setError('email', mail && !validEmail(mail) ? "That email doesn't look right. Fix it or clear the field." : '');
      }
      var query = input.value;
      if (!normalize(query)) { setError('food', 'Type a food, like turkey or grapes.'); input.focus(); return; }
      setError('food', '');
      var record = active >= 0 && options[active] ? byHandle[options[active].dataset.handle] : bestMatch(query, records);
      closeList();
      if (record) answer(record);
      else unknown(query);
    });
    form.addEventListener('input', function (event) {
      var name = event.target && event.target.name;
      if (name === 'food') setError('food', '');
      if (name === 'weight_lb') setError('weight', '');
      if (name === 'email') setError('email', '');
    });

    /* Klaviyo: fire and forget. The visitor already has the answer. */
    function subscribe(record, unknownQuery, emailOverride) {
      var email = emailOverride || emailValue();
      if (!emailEnabled || !email || typeof win.fetch !== 'function') return false;
      if (form.elements.website && form.elements.website.value) return false;
      var key = email.toLowerCase() + '|' + (record ? record.h : 'unknown:' + unknownQuery);
      if (sentFor[key]) return true;
      var body;
      try {
        body = JSON.stringify(subscriptionPayload({
          email: email, pet_name: dogName(), weight_lb: dogWeight(),
          food: record ? record.n : '', verdict: record ? record.v : '',
          unknown: record ? '' : unknownQuery,
          foods_checked: checked.map(function (h) { return byHandle[h] ? byHandle[h].n : h; }),
          placement: placement
        }, config.listId));
      } catch (_) { return false; }
      sentFor[key] = true;
      var controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      var timeout = controller ? win.setTimeout(function () { controller.abort(); }, 15000) : 0;
      function failed() { delete sentFor[key]; }
      try {
        win.fetch(KLAVIYO_URL + encodeURIComponent(config.companyId), {
          method: 'POST',
          headers: { Accept: 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: body,
          signal: controller ? controller.signal : undefined
        }).then(function (response) {
          win.clearTimeout(timeout);
          if (response.status !== 202) { failed(); return; }
          track('email_submitted', { food: record ? record.h : null, verdict: record ? record.v : null });
        }).catch(function () { win.clearTimeout(timeout); failed(); });
      } catch (_) { win.clearTimeout(timeout); failed(); }
      return true;
    }

    /* Rendering. Everything goes in with textContent: record text is data, never HTML. */
    function telLink(label, number) {
      var a = el('a', '', label + ' ' + number);
      a.href = 'tel:' + String(number).replace(/[^0-9+]/g, '');
      return a;
    }
    function poisonLine(container, urgent) {
      var box = el('div', 'pn-food__poison' + (urgent ? ' pn-food__poison--urgent' : ''));
      if (urgent) box.appendChild(el('p', 'pn-food__poison-title', 'If she already ate some, call now. Don’t wait for signs.'));
      var p = el('p', 'pn-food__poison-text');
      p.appendChild(doc.createTextNode('Call your vet, or '));
      if (poison.aspca) p.appendChild(telLink('ASPCA Animal Poison Control', poison.aspca));
      if (poison.helpline) { p.appendChild(doc.createTextNode(' or ')); p.appendChild(telLink('Pet Poison Helpline', poison.helpline)); }
      p.appendChild(doc.createTextNode('. Both answer 24/7 and charge a fee.'));
      box.appendChild(p);
      container.appendChild(box);
    }
    function block(container, title, text) {
      if (!text) return;
      var wrap = el('div', 'pn-food__block');
      wrap.appendChild(el('p', 'pn-food__block-title', title));
      wrap.appendChild(el('p', 'pn-food__block-text', text));
      container.appendChild(wrap);
    }
    function chips(container, title, items) {
      if (!items.length) return;
      var wrap = el('div', 'pn-food__related');
      wrap.appendChild(el('p', 'pn-food__block-title', title));
      var row = el('div', 'pn-food__chips');
      items.forEach(function (r) {
        var chip = el('button', 'pn-food__chip');
        chip.type = 'button';
        chip.setAttribute('data-pn-food-pick', r.h);
        chip.setAttribute('aria-label', r.n + ': ' + VERDICT_LABEL[r.v]);
        chip.appendChild(el('span', 'pn-food__dot pn-food__dot--' + r.v));
        chip.appendChild(el('span', '', r.n));
        row.appendChild(chip);
      });
      wrap.appendChild(row);
      container.appendChild(wrap);
    }
    function amountRows(container, rows) {
      var ul = el('ul', 'pn-food__amount-list');
      rows.forEach(function (r) {
        var li = el('li', '');
        li.appendChild(el('span', 'pn-food__amount-band', r.label));
        li.appendChild(el('span', 'pn-food__amount-text', r.text));
        ul.appendChild(li);
      });
      container.appendChild(ul);
    }
    function emailPrompt(record, unknownQuery) {
      if (!emailEnabled) return;
      var name = dogName();
      var box = el('form', 'pn-food__signup');
      box.setAttribute('novalidate', '');
      box.setAttribute('action', '#');
      box.setAttribute('method', 'post');
      var title;
      if (!record) title = 'Want to know when we’ve reviewed it?';
      else if (record.v === 'toxic') title = 'Email me this emergency card';
      else title = name ? 'Save ' + possessive(name) + ' food list' : 'Save your dog’s food list';
      box.appendChild(el('p', 'pn-food__signup-title', title));
      box.appendChild(el('p', 'pn-food__signup-text', !record
        ? 'Dr. Adam reviews new foods every week. We’ll email you when this one is done, plus seasonal alerts like the Thanksgiving table and Halloween candy.'
        : 'Every food you check here in one email, plus seasonal food alerts (Thanksgiving, Halloween, the holidays) and new foods as Dr. Adam reviews them.'));
      var already = emailValue();
      if (already) {
        subscribe(record, unknownQuery, already);
        box.appendChild(el('p', 'pn-food__signup-done', 'Added. It’s on its way to ' + already + '.'));
        result.appendChild(box);
        return;
      }
      var row = el('div', 'pn-food__signup-row');
      var fieldId = uid + '-signup';
      var label = el('label', 'sr-only', 'Email');
      label.setAttribute('for', fieldId);
      var field = el('input', 'pn-calc__input');
      field.type = 'email'; field.id = fieldId; field.name = 'signup_email'; field.maxLength = 254;
      field.setAttribute('autocomplete', 'email'); field.setAttribute('inputmode', 'email');
      field.setAttribute('autocapitalize', 'none'); field.setAttribute('spellcheck', 'false');
      field.placeholder = 'you@example.com';
      var button = el('button', 'btn btn-coral pn-food__signup-btn', 'Email it to me');
      button.type = 'submit';
      row.appendChild(label); row.appendChild(field); row.appendChild(button);
      box.appendChild(row);
      var error = el('p', 'pn-calc__error');
      error.hidden = true;
      error.setAttribute('role', 'alert');
      box.appendChild(error);
      box.appendChild(el('p', 'fine pn-calc__note', 'By adding your email you agree to get emails from Pet Nugget. Unsubscribe anytime.'));
      box.addEventListener('submit', function (event) {
        event.preventDefault();
        var mail = String(field.value || '').trim();
        if (!validEmail(mail)) { error.textContent = "That email doesn't look right."; error.hidden = false; field.setAttribute('aria-invalid', 'true'); field.focus(); return; }
        if (emailInput) emailInput.value = mail;
        subscribe(record, unknownQuery, mail);
        box.textContent = '';
        box.appendChild(el('p', 'pn-food__signup-title', 'Done. Check your inbox.'));
        box.appendChild(el('p', 'pn-food__signup-text', 'We’ll send it to ' + mail + '.'));
      });
      result.appendChild(box);
    }
    function show() {
      result.hidden = false;
      root.setAttribute('data-pn-food-state', 'answer');
      var head = result.querySelector('[data-pn-food-answer-head]');
      if (!head) return;
      try { head.focus({ preventScroll: true }); } catch (_) { head.focus(); }
      var top = result.getBoundingClientRect().top;
      if (top < 80 || top > win.innerHeight * 0.7) result.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    function answer(record) {
      remember(record);
      result.textContent = '';
      var name = dogName();
      var lb = dogWeight();
      var card = el('div', 'pn-food__answer pn-food__answer--' + record.v);
      var top = el('div', 'pn-food__answer-top');
      top.appendChild(el('span', 'pn-food__verdict pn-food__verdict--' + record.v, VERDICT_LABEL[record.v]));
      var head = el('p', 'pn-food__answer-q', question(record, name));
      head.setAttribute('tabindex', '-1');
      head.setAttribute('data-pn-food-answer-head', '');
      top.appendChild(head);
      card.appendChild(top);

      // Toxic: emergency steps come first, before anything else.
      if (record.v === 'toxic') {
        poisonLine(card, true);
        block(card, 'What to do', record.todo);
      }
      card.appendChild(el('p', 'pn-food__answer-text', record.ans));

      if (Array.isArray(record.f) && record.f.length) {
        var forms = el('div', 'pn-food__block');
        forms.appendChild(el('p', 'pn-food__block-title', 'Raw, cooked and everything in between'));
        var ul = el('ul', 'pn-food__forms');
        record.f.forEach(function (item) {
          if (!item || !item.form) return;
          var li = el('li', 'pn-food__form');
          if (VERDICTS.indexOf(item.verdict) !== -1) li.appendChild(el('span', 'pn-food__badge pn-food__badge--' + item.verdict, VERDICT_LABEL[item.verdict]));
          var copy = el('span', 'pn-food__form-copy');
          copy.appendChild(el('strong', '', item.form));
          if (item.note) copy.appendChild(doc.createTextNode(' ' + item.note));
          li.appendChild(copy);
          ul.appendChild(li);
        });
        forms.appendChild(ul);
        card.appendChild(forms);
      }

      if (Array.isArray(record.am) && record.am.length) {
        var amount = el('div', 'pn-food__block pn-food__amount');
        var row = amountFor(record.am, lb);
        var toxic = record.v === 'toxic';
        if (row) {
          amount.appendChild(el('p', 'pn-food__block-title', (toxic ? 'How much is a problem for ' : 'How much for ') + (name || 'her') + ' (' + lb + ' lb)'));
          amountRows(amount, [row]);
        } else {
          amount.appendChild(el('p', 'pn-food__block-title', toxic ? 'How much is a problem, by size' : 'How much, by size'));
          amountRows(amount, record.am);
          if (weightInput && !lb) amount.appendChild(el('p', 'pn-food__hint', 'Add her weight above to see just her row.'));
        }
        if (record.an) amount.appendChild(el('p', 'pn-food__hint', record.an));
        card.appendChild(amount);
      }

      block(card, 'Why', record.cmp);
      block(card, 'Signs to watch for', record.sig);
      if (record.v !== 'toxic') block(card, 'If she ate too much', record.todo);
      if (record.v === 'skip' || record.v === 'little') poisonLine(card, false);

      var foot = el('div', 'pn-food__answer-foot');
      var url = safeUrl(record.u);
      if (url) {
        var more = el('a', 'link pn-food__more', record.ut || 'Read the full guide');
        more.href = url;
        foot.appendChild(more);
      }
      if (Array.isArray(record.src) && record.src.length) {
        var srcP = el('p', 'fine pn-food__sources');
        srcP.appendChild(doc.createTextNode('Sources: '));
        record.src.slice(0, 4).forEach(function (src, i) {
          var href = safeUrl(src && src.url);
          if (!href) return;
          if (i) srcP.appendChild(doc.createTextNode(', '));
          var a = el('a', '', src.name || href);
          a.href = href; a.target = '_blank'; a.rel = 'noopener';
          srcP.appendChild(a);
        });
        foot.appendChild(srcP);
      }
      // Only records Adam has signed off carry a reviewer. The rest say where they come from.
      foot.appendChild(el('p', 'fine pn-food__reviewed', record.rv
        ? 'Reviewed by ' + record.rv + (record.rd ? ' · ' + record.rd : '')
        : 'From published veterinary sources · vet review pending'));
      card.appendChild(foot);

      var related = (Array.isArray(record.r) ? record.r : []).map(function (h) { return byHandle[h]; }).filter(function (r) { return r && r.h !== record.h; }).slice(0, 5);
      chips(card, 'Check next', related);
      result.appendChild(card);
      emailPrompt(record, '');
      updateChecked();
      show();
      track('search', { food: record.h, verdict: record.v });
    }

    function unknown(query) {
      var shown = String(query || '').trim().slice(0, 60);
      result.textContent = '';
      var card = el('div', 'pn-food__answer pn-food__answer--unknown');
      var head = el('p', 'pn-food__answer-q', 'We haven’t reviewed “' + shown + '” yet');
      head.setAttribute('tabindex', '-1');
      head.setAttribute('data-pn-food-answer-head', '');
      card.appendChild(head);
      card.appendChild(el('p', 'pn-food__answer-text', 'We only show answers our veterinarian has checked, so we won’t guess. If she already ate some and you’re worried, call now.'));
      poisonLine(card, false);
      chips(card, 'Did you mean', search(query, records, 4));
      result.appendChild(card);
      emailPrompt(null, normalize(query) || shown);
      show();
      track('search_unknown', { query: normalize(query).slice(0, 60) });
    }

    function updateChecked() {
      if (!checkedBox) return;
      var foods = checked.map(function (h) { return byHandle[h]; }).filter(Boolean).reverse();
      checkedBox.textContent = '';
      checkedBox.hidden = !foods.length;
      var name = dogName();
      chips(checkedBox, name ? possessive(name) + ' list so far' : 'Foods you’ve checked', foods);
    }
    updateChecked();

    /* Fridge chart: print the A-Z chart with her name on it */
    function updatePrintName() {
      var name = dogName();
      doc.querySelectorAll('[data-pn-food-print-name]').forEach(function (node) {
        node.textContent = name ? possessive(name) + ' fridge chart: can she eat it?' : 'Can my dog eat it? Fridge chart';
      });
    }
    updatePrintName();
    if (nameInput) nameInput.addEventListener('change', function () { remember(null); updateChecked(); updatePrintName(); });
    if (weightInput) weightInput.addEventListener('change', function () { remember(null); });
    doc.querySelectorAll('[data-pn-food-print]').forEach(function (button) {
      if (button.dataset.pnFoodPrintBound) return;
      button.dataset.pnFoodPrintBound = 'true';
      button.addEventListener('click', function () {
        updatePrintName();
        track('print_chart', {});
        win.print();
      });
    });

    // ?food=<handle> (from a post's verdict box, or a shared link) opens straight on that food.
    try {
      var wanted = new win.URLSearchParams(win.location.search).get('food');
      var hit = wanted ? (byHandle[wanted] || bestMatch(wanted, records)) : null;
      if (hit) { input.value = hit.n; answer(hit); }
    } catch (_) {}
    root.setAttribute('data-pn-food-state', result.hidden ? 'ready' : 'answer');
  }

  return {
    mount: mount, normalize: normalize, search: search, bestMatch: bestMatch, confidentScore: confidentScore, distance: distance,
    parseWeight: parseWeight, weightBand: weightBand, sizeFor: sizeFor, amountFor: amountFor,
    question: question, possessive: possessive, subscriptionPayload: subscriptionPayload,
    validEmail: validEmail, validId: validId, safeUrl: safeUrl, VERDICT_LABEL: VERDICT_LABEL
  };
});
